import React, { useEffect, useRef, useState } from 'react';
import { Button, message, Upload, Image, Spin, Modal, Row, Col } from 'antd';
import MessageConfirmModal from '../messageModal/MessageConfirmModal';
import UploadColorSvg from '../../assets/images/uploadColorSvg';
import { useTranslation } from 'react-i18next';
import ChangeCoverSvg from '../../assets/images/ChangeCoverSvg';
import { getDataRequest, postDataRequest } from '../../api/serviceMethods';
import { GET_PRIVATE_URL, UPLOAD_PUBLIC_URL, UPLOAD_VIDEO_GET_URL, UPLOAD_VIDEO_URL } from '../../api/URLs';
import { RemoveSvg } from '../../assets/images/RemoveSvg';
import { formatFileSize, getFileType } from '../../utils/string';
import TagsSvg from '../../assets/images/TagsSvg';
import { VideoImageComponent } from '../image/ImageComponent';

const { Dragger } = Upload;

const DragUploadTeacherComponentVideo = ({
    size = 1,
    fileList,
    setFileList,
    previewUrl,
    isError = false,
    isRequire = false,
    uploadavatarTitle = "",
    changeAvatarTitle = "",
    allowType = [
        "video/mp4",         // .mp4
        "video/x-ms-wmv",    // .wmv
        "video/x-msvideo",   // .avi
    ],
    titleallowFile = "",
    maxSize, apiUrl, isview,
    customRequest,
    loading: externalLoading
}) => {
    const messageModalRef = useRef("");
    const { i18n, t } = useTranslation();

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const uploadRef = useRef(null);
    const [loading, setLoading] = useState(false);

    // Convert image file to base64
    const getBase64 = (img, callback) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result));
        reader.readAsDataURL(img);
    };

    // Before upload validation
    const beforeUpload = async (file) => {

        const duration = await getVideoDurationFromFile(file);
        console.log("🎞 Video duration:", duration, "seconds");
        // if (duration > 600) {
        //     message.error("Video is too long (max 10 minutes)");
        //     return Upload.LIST_IGNORE;
        // }

        if (!file.type.startsWith("video/")) {
            message.error("Only video files are allowed!");
            return false;
        }

        const isLt500M = file.size / 1024 / 1024 < 500; // ✅ 500 MB limit
        if (!isLt500M) {
            message.error("Video must be smaller than 500MB!");
            return false;
        }

        return true;
    };


    // Handle preview for image files
    const handlePreviewImage = async (file) => {
        setPreviewImage(previewUrl ?? file.url);
        setPreviewOpen(true);
    };

    // Handle preview for image files
    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await new Promise((resolve) => {
                getBase64(file.originFileObj, resolve);
            });
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const getFilePage = async (file) => {
        return new Promise((resolve, reject) => {
            if (!file) return resolve(0);

            const reader = new FileReader();

            reader.onloadend = () => {
                // Only try to count pages for PDFs
                if (file.type === "application/pdf") {
                    const matches = reader.result.match(/\/Type[\s]*\/Page[^s]/g);
                    const count = matches ? matches.length : 0;
                    resolve(count);
                } else {
                    // ✅ Not a PDF — probably a video or other file
                    resolve(0);
                }
            };

            reader.onerror = (error) => reject(error);
            reader.readAsBinaryString(file);
        });
    };

    const getVideoDurationFromFile = (file) => {
        return new Promise((resolve, reject) => {
            try {
                const video = document.createElement("video");
                video.preload = "metadata";

                const fileURL = URL.createObjectURL(file);
                video.src = fileURL;

                video.onloadedmetadata = () => {
                    URL.revokeObjectURL(fileURL);
                    resolve(video.duration);
                };

                video.onerror = (err) => {
                    URL.revokeObjectURL(fileURL);
                    reject(err);
                };
            } catch (error) {
                reject(error);
            }
        });
    };

    const geUploadById = async (id) => {
        return await getDataRequest(`${UPLOAD_VIDEO_GET_URL}/${id && id}`);
    }

    // Convert base64 → Blob (binary)
    const dataURLtoFile = (dataurl, filename) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    const getVideoThumbnailFromFile = (file, captureTime = 2) => {
        return new Promise((resolve, reject) => {
            try {
                const video = document.createElement("video");
                video.preload = "metadata";
                const fileURL = URL.createObjectURL(file);
                video.src = fileURL;
                video.crossOrigin = "anonymous";

                video.onloadeddata = () => {
                    // Ensure the capture time is within video duration
                    video.currentTime = Math.min(captureTime, video.duration / 2);
                };

                video.onseeked = () => {
                    try {
                        const canvas = document.createElement("canvas");
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const dataUrl = canvas.toDataURL("image/png");
                        URL.revokeObjectURL(fileURL);
                        resolve(dataUrl);
                    } catch (err) {
                        reject(err);
                    }
                };

                video.onerror = (err) => {
                    URL.revokeObjectURL(fileURL);
                    reject(err);
                };
            } catch (err) {
                reject(err);
            }
        });
    };

    // Example: upload thumbnail binary file
    const uploadThumbnailToAPI = async (thumbnailFile) => {
        const formData = new FormData();
        formData.append("file", thumbnailFile); // ✅ this is file: (binary)

        const response = await postDataRequest(UPLOAD_PUBLIC_URL, formData, {
            headers: {
                'Content-Type': 'multipart/form-data', // Ensure the correct header for file upload
            },
        });
        return response?.fileUrl ? response?.fileUrl : null
    };


    // Custom upload logic using axios and FormData
    const handleCustomUpload = async (options) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);

            const [count, response] = await Promise.all([
                getFilePage(file),
                uploadPrivateFile(formData),
            ]);
            const uploadId = response && response?.uploadId ? response?.uploadId : "";
            if (!uploadId) message.error('No file ID returned from upload');
            const duration = await getVideoDurationFromFile(file) || 0;
            console.log("duration...", duration);


            const thumbnailDataUrl = isview ? await getVideoThumbnailFromFile(file) : '';
            // Convert base64 → File (binary)
            const thumbnailFile = isview ? dataURLtoFile(thumbnailDataUrl, `${file.name}_thumbnail.png`) : '';
            // Upload thumbnail binary
            const thumbnailUploadResponse = isview ? await uploadThumbnailToAPI(thumbnailFile) : '';

            let attempt = 0;
            let isPolling = true;
            let uploadedFile = null;

            do {
                attempt++;
                console.log(`Polling attempt ${attempt} for ID: ${uploadId}`);

                const result = await geUploadById(uploadId);

                if (!result) {
                    console.log("No response yet, continue polling...");
                } else if (result.status === "uploading") {
                    // Still uploading, keep polling
                    console.log("Still uploading, will check again...");
                } else if (result.status === "complete" || result.status === "ready") {
                    uploadedFile = result;
                    console.log("✅ Upload complete!");
                    isPolling = false; // stop loop
                } else if (result.status === "failed" || result.error) {
                    uploadedFile = result;
                    console.error("❌ Upload failed!");
                    isPolling = false; // stop loop
                }

                if (isPolling) {
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                }

            } while (isPolling);

            if (!uploadedFile || uploadedFile && uploadedFile.status !== "complete") {
                setLoading(false);
                throw new Error('File not available after waiting');
            }


            if (uploadedFile?.url) {
                uploadedFile.fileUrl = uploadedFile?.url
                uploadedFile.name = uploadedFile?.filename
                // Check the response and use the fileUrl from the response
                onSuccess(uploadedFile);
                // onSuccess({
                //     "message": "File uploaded successfully.",
                //     "fileUrl": "https://esipro-dev.oss-cn-shanghai.aliyuncs.com/public/1760339622069-Screenshot%202025-10-13%20at%202.13.33%C3%A2%C2%80%C2%AFPM.png",
                //     "filename": "public/1760339622069-Screenshot 2025-10-13 at 2.13.33â¯PM.png"
                // });

                if (size === 1) {
                    setFileList([
                        {
                            page: count,
                            uid: file?.uid,
                            name: file?.name,
                            status: 'done',
                            url: uploadedFile.fileUrl,
                            size: file?.size,
                            duration: parseInt(duration),
                            thumbnail: thumbnailUploadResponse
                        }
                    ]);
                } else {
                    setFileList((prev) => [
                        ...prev,
                        {
                            page: count,
                            uid: file?.uid,
                            name: file?.name,
                            status: 'done',
                            url: uploadedFile?.fileUrl,
                            size: file?.size,
                            duration: parseInt(duration),
                            thumbnail: thumbnailUploadResponse
                        },
                    ]);
                }

                setLoading(false);
            } else {
                setLoading(false);
                throw new Error('Invalid response structure');
            }
        } catch (error) {
            setLoading(false);
            onError(error);
            message.error(error?.error || 'Upload failed. Please try again.');
        }
    };

    const uploadPrivateFile = async (formData) => {
        return await postDataRequest(apiUrl ? apiUrl : UPLOAD_VIDEO_URL, formData, {
            headers: {
                'Content-Type': 'multipart/form-data', // Ensure the correct header for file upload
            },
        });
    }

    // Handle file removal
    const handleRemove = (file) => {
        const updatedFileList = fileList.filter((item) => item.uid !== file.uid);
        setFileList(updatedFileList);
    };

    // Handle change for fileList
    const handleChange = (info) => {
        if (info.file.status === 'done') {
            // message.success(`${info.file.name} file uploaded successfully.`);
        } else if (info.file.status === 'error') {
            // message.error(`${info.file.name} file upload failed.`);
        }
    };

    // Change cover handler
    const handleChangeCover = () => {
        uploadRef.current.click();
    };

    const props = {
        beforeUpload,
        customRequest: customRequest || handleCustomUpload,
        multiple: false,
        onChange: handleChange,
        onDrop: (e) => {
            console.log("Dropped files", e.dataTransfer.files);
        },
        onPreview: handlePreview,
        className: 'newUpload',
        showUploadList: false,
        onRemove: handleRemove,
        fileList: fileList,
        accept: "video/*"
    };

    return (
        <>
            <div className={`w-100 ${fileList.length > 0 && size === 1 ? 'd-none' : ''} `}>
                <Dragger  {...props}>
                    <Spin spinning={!!(loading || externalLoading)}>
                        <>
                            {
                                size > 1 && fileList && fileList.length > 0 ? fileList.map((it, i) => (
                                    <div className='d-flex mt-1 mt-1 justify-content-between align-items-center ps-4 pe-4 pt-4 pb-4' style={{ background: "rgb(0 0 0 / 2%)", borderRadius: "8px" }}>
                                        <VideoComponent it={it} isview={isview} />
                                        {/* <div className='d-flex align-items-center'>
                                            <div className='pe-3'>
                                                <img 
                                                // onClick={(() => handlePreviewImage(it))} 
                                                style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover" }} 
                                                src={`${it.url}`}
                                                 />
                                            </div>
                                            <div className='text-image'>
                                                <div style={{ textAlign: "left" }}>{it.name || 'NA'}</div>
                                                <div style={{ textAlign: "left" }}>
                                                    <span className='text-uppercase text-wrap-nowrap'>{it.name && getFileType(it.name)}</span>
                                                    <span className='ps-2' style={{ paddingBottom: "6px" }}>.</span>
                                                    <span className='ps-2'>{it.size ? formatFileSize(it.size) : `0 MB`}</span>

                                                </div>
                                            </div>
                                        </div> */}
                                        <div>
                                            <span className='remove-icon' onClick={() => {
                                                fileList.splice(i, 1);
                                                setFileList([...fileList])
                                            }} style={{ cursor: "pointer" }}><RemoveSvg /></span>
                                        </div>
                                    </div>
                                )) : ''
                            }
                            {size > 1 && fileList && fileList.length === 0 || size === 1 ? <>
                                <Button
                                    style={{
                                        height: "48px",
                                        width: "48px",
                                        backgroundColor: "#F5F7F9",
                                        border: "none"
                                    }}
                                    ref={uploadRef}
                                >
                                    <UploadColorSvg />
                                </Button>
                                <div className='mt-2'>
                                    <p className="ant-upload-text mt-2">
                                        {t("Drag files here or")}
                                        <a href="#!" style={{ marginLeft: "5px", textDecoration: "underline" }}>{t("Select File")}</a>
                                    </p>
                                    <p className="ant-upload-hint">
                                        {titleallowFile ? titleallowFile : t(`Supports mp4/wmv/avi and other formats, with a maximum file size of 500MB`)}
                                    </p>
                                </div>
                            </> : ''}
                        </>
                    </Spin>
                </Dragger>
            </div>

            {fileList && fileList.length > 0 && size === 1 ? <div className='ant-upload-wrapper-cus w-100'>
                <div className='ant-upload ant-upload-drag align-content-center pe-3 ps-3 pt-4 pb-4'>
                    {
                        fileList && fileList.length > 0 ? fileList.map((it, i) => (
                            <div className='d-flex mt-1 mt-1 justify-content-between align-items-center ps-4 pe-4 pt-4 pb-4' style={{ background: "rgb(0 0 0 / 2%)", borderRadius: "8px" }}>
                                {/* <div className='d-flex align-items-center'>
                                    <div className='pe-3'>
                                        <img onClick={(() => handlePreviewImage(it))} style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover" }} src={`${previewUrl}`} />
                                    </div>
                                    <div className='text-image'>
                                        <div style={{ textAlign: "left" }}>{it.name || 'NA'}</div>
                                        <div style={{ textAlign: "left" }}>
                                            <span className='text-uppercase text-wrap-nowrap'>{it.name && getFileType(it.name)}</span>
                                            <span className='ps-2' style={{ paddingBottom: "6px" }}>.</span>
                                            <span className='ps-2'>{it.size ? formatFileSize(it.size) : `0 MB`}</span>
                                            <span className='ps-2' style={{ paddingBottom: "6px" }}>.</span>
                                            {it.page && <span className='ps-2'>{it.page + ` ` + t("pageSize Pdf")}</span>}
                                        </div>
                                    </div>
                                </div> */}
                                <VideoComponent it={it} previewUrl={previewUrl} handlePreviewImage={handlePreviewImage} isview={isview} />
                                <div>
                                    <span onClick={() => {
                                        fileList.splice(i, 1);
                                        setFileList([...fileList])
                                    }} style={{ cursor: "pointer" }}><RemoveSvg /></span>
                                </div>
                            </div>
                        )) : ''
                    }
                </div>
            </div> : ''}

            {fileList.length >= 1 && (
                <div className="mt-3 d-none text-center d-flex align-items-center">
                    <ChangeCoverSvg className="me-3" />
                    <a
                        href="#!"
                        onClick={handleChangeCover}
                        style={{ marginLeft: '10px' }}
                    >
                        <span className="span-danger">*</span> {changeAvatarTitle ? changeAvatarTitle : t('Change avatar')}
                    </a>
                </div>
            )}
            {previewImage && (
                <Image
                    wrapperStyle={{
                        display: 'none',
                    }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) =>
                            !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
            <MessageConfirmModal textCentered ref={messageModalRef} />
        </>
    );
};


export const VideoThumbnail = (props) => {
    const { url, time = 2, it, handlePreviewImage, isview, previewUrl } = props
    const [viewVideo, setViewVideo] = useState(url);
    const [thumbnail, setThumbnail] = useState(null);
    const [modalOpen, setModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const getViewThumb = async() => {
        try {
            const video = document.createElement("video");
            video.src = url;
            video.crossOrigin = "anonymous"; // required for external video
            video.currentTime = time; // pick frame at 2 seconds

            setLoading(true)
            video.addEventListener("loadeddata", async () => {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL("image/png");
                setThumbnail(dataUrl);
                setLoading(false)
            });
            video.addEventListener("error", () => {
                console.error("Video failed to load");
                setLoading(false)
            });
        } catch (error) {
            console.log(`error: `, error)
            
        }
        
    }
    useEffect(() => {
        getViewThumb()
    }, [url, time]);


    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const handlePreviewImages = async (file) => {
        if(isview) {
            handleView(file)
        }
        else {
            // setPreviewImage(file.url);
            // setPreviewOpen(true);
            setModalOpen(true)
        }
    };

    const handleView = async(file) => {
        
        try {
            let lastPath = new URL(file?.url && file.url)?.pathname;
            if (lastPath) {
                const cleanedPath = lastPath.startsWith('/') ? lastPath.slice(1) : lastPath;
                const response = await getDataRequest(GET_PRIVATE_URL + `?filename=${cleanedPath}`);
                setViewVideo(response?.fileUrl)
                setLoading(false);
                setModalOpen(true)
            } else {
                message.error('Request viewed failed. Please try again.');
            }
        } catch (error) {
            setLoading(false);
            message.error('Request viewed failed. Please try again.');
        }

    }

    return (
        <div>
            {previewImage && (
                <Image
                    wrapperStyle={{
                        display: 'none'
                    }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) =>
                            !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
            {modalOpen ? <VideoModal modalOpen={modalOpen} loadingNew={false} setModalOpen={setModalOpen}
            videoUrl={viewVideo}
             /> : ""}
            {
                isview ? <img
                    onClick={(() => handlePreviewImages && handlePreviewImages({
                        ...it,
                        url: viewVideo
                    }))}
                    // onClick={(() => handlePreviewImage && handlePreviewImage({
                    //     ...it,
                    //     url: thumbnail
                    // }))}
                    style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover", cursor: "pointer" }}
                    src={`${it?.thumbnail}`}
                    alt="video thumbnail"
                /> :
                    <>
                        {!loading ? (
                            thumbnail ? <img
                                onClick={(() => handlePreviewImages && handlePreviewImages({
                                    ...it,
                                    url: viewVideo
                                }))}
                                // onClick={(() => handlePreviewImage && handlePreviewImage({
                                //     ...it,
                                //     url: thumbnail
                                // }))}
                                style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover", cursor: "pointer" }}
                                src={`${previewUrl || thumbnail}`}
                                alt="video thumbnail"
                            />
                                // <img
                                //   src={thumbnail}
                                //   alt="video thumbnail"
                                //   style={{ width: "320px", height: "180px", objectFit: "cover" }}
                                // /> 
                                : <span
                                    onClick={(() => handlePreviewImages && handlePreviewImages({
                                        ...it,
                                        url: viewVideo
                                    }))}
                                    style={{ cursor: "pointer" }}
                                ><VideoImageComponent scr={previewUrl || thumbnail} /></span>
                        ) : (
                            <>
                                <p>Loading...</p>
                            </>
                        )}
                    </>
            }
             
        </div>
    );
};

export const VideoComponent = (props) => {
    const { it, handlePreviewImage, isview, previewUrl } = props

    return <>
        <div className='d-flex align-items-center'>
            <div className='pe-3'>
                <VideoThumbnail handlePreviewImage={handlePreviewImage} previewUrl={previewUrl} isview={isview} url={it.url} it={it} />
                {/* <img
                    onClick={(() => handlePreviewImage && handlePreviewImage(it))} 
                    style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover" }}
                    src={`${it.url}`}
                /> */}
            </div>
            <div className='text-image'>
                <div style={{ textAlign: "left" }}>{it.name || 'NA'}</div>
                <div style={{ textAlign: "left" }}>
                    <span className='text-uppercase text-wrap-nowrap'>{it.url && getFileType(it.url)}</span>
                    <span className='ps-2' style={{ paddingBottom: "6px" }}>.</span>
                    <span className='ps-2'>{it.size ? formatFileSize(it.size) : `0 MB`}</span>

                </div>
            </div>
        </div>
    </>
}


export const VideoModal = (props) => {
    const { setModalOpen, modalOpen, loadingNew, videoUrl } = props
    const { t } = useTranslation();
    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {/* <TagsSvg />
                    <span style={{ marginLeft: "10px" }}>{t('Video')}</span> */}
                </div>
            }
            centered
            width={700}
            open={modalOpen}
            className='custom-modal'
            onOk={() => setModalOpen(false)}
            onCancel={() => {
                setModalOpen(false)
            }}
            cancelButtonProps={{ style: { display: 'none' } }}
            okButtonProps={{ style: { display: 'none' } }}
        >
            <Spin spinning={loadingNew}>
                <div className='d-flex align-items-baseline pt-3 ' style={{ width: "100%" }}>
                    <video
                        width="100%"
                        height="27rem"
                        controls
                        style={{height: "27rem"}}
                        poster="" // optional image thumbnail
                        autoPlay      // ✅ auto play
                        muted={false}         // ✅ required for autoplay in most browsers
                        loop          // optional: loops the video
                    >
                        <source src={videoUrl} type="video/mp4" />
                        <source src={videoUrl} type="video/webm" />
                        <source src={videoUrl} type="video/quicktime" />
                        Your browser does not support the video tag.
                    </video>
                </div>
                
            </Spin>
        </Modal>
    )
}

export default DragUploadTeacherComponentVideo;
